import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import TemplatePageDetailsModal from '@modals/TemplatePageDetailsModal'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { TemplatePageModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showTemplatePageDetailsModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    TemplatePageDetailsModal: {
      bulkUpdatePermissions: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showTemplatePageDetailsModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showTemplatePageDetailsModal } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showTemplatePageDetailsModal: (payload: TemplatePageModel) => {
        setModalData('TemplatePageDetailsModal', payload)
        setState({ showTemplatePageDetailsModal: true })
      },
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <TemplatePageDetailsModal
          callbacks={callbacks('TemplatePageDetailsModal', setState)}
          showModal={showTemplatePageDetailsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
